import type { ReactNode } from 'react';
import { Box, type BoxProps } from '@chakra-ui/react';
import { useKFlags } from '@/hooks/use-k-flags';
import { AutomationPreviewBody } from './AutomationPreviewBody';
import { useStageAutomationRule } from './useStagesWithAutomation';
import { MOVE_WITH_AUTOMATION_STRINGS } from './moveWithAutomation.strings';

export type StageAutomationPreviewProps = {
  // The stage the candidate will land in. Null/empty disables the fetch and
  // renders nothing.
  stageId: string | number | null | undefined;
  // Optional explicit stage name; falls back to the rule's own stage name
  // (`summary.ats_job_stage.name`) and then the generic "Stage" label.
  stageName?: string;
  dataTestId?: string;
  // Spread onto the outer container. Defaults to `my="16px"`; a caller can pass
  // `{ my: 0 }` (or `{ mt: 0 }`) to render the preview flush inline.
  containerProps?: BoxProps;
  // Rendered while the stage's rule is still in flight. Only surfaces that open
  // on demand need it (e.g. a hover popover, which would otherwise flash an
  // empty panel); inline previews leave it undefined and render nothing.
  loadingFallback?: ReactNode;
  // Forwarded to the body: cap + scroll the activity list (default true). Pass
  // `false` for an inline preview inside an already-scrollable surface so it
};

// Self-contained automation preview for a single target stage. Fetches the
// stage's active workflow_rule and renders the read-only activity/timing
// summary only when the stage actually has automation. Each instance owns its
// own query, so it's safe to render one per selected stage in a list (e.g. the
// multi-job create-candidate flow) — something a single top-level hook can't do.
export const StageAutomationPreview = ({
  stageId,
  stageName,
  dataTestId = 'stage-automation-preview',
  containerProps,
  loadingFallback,
}: StageAutomationPreviewProps) => {
  const { feStageAutomation } = useKFlags();
  const { data: rule, isLoading } = useStageAutomationRule(stageId, {
    enabled: feStageAutomation,
  });

  if (!feStageAutomation) return null;

  if (isLoading && loadingFallback) {
    return (
      <Box my="16px" {...containerProps} data-test-id={`${dataTestId}-loading`}>
        {loadingFallback}
      </Box>
    );
  }

  if ((rule?.actions?.length ?? 0) === 0) return null;

  const resolvedStageName =
    stageName ??
    rule?.summary?.ats_job_stage?.name ??
    MOVE_WITH_AUTOMATION_STRINGS.fallback.stageName;

  return (
    <Box my="16px" {...containerProps}>
      <AutomationPreviewBody
        targetStageName={resolvedStageName}
        targetRule={rule}
        dataTestId={dataTestId}
      />
    </Box>
  );
};
